import { create } from 'zustand';
import {
  emptyAdsConfig,
  type AdScreenName,
  type AdScreenSlotName,
  type AdSideSlotName,
  type AdsConfig,
} from '@anon-chat/shared';

/**
 * Телефон определяется шириной окна, а не строкой браузера: рекламные сети
 * выдают блоки под ширину, и подмена устройства в настройках браузера должна
 * менять блок так же, как настоящий телефон.
 */
const MOBILE_QUERY = '(max-width: 767px)';

export type InterstitialTrigger = 'chatEnd' | 'searchStart';

interface AdsState {
  config: AdsConfig;
  isMobile: boolean;
  /** Экран, на котором пользователь сейчас: боковые полосы видны не на всех. */
  screen: AdScreenName;
  /** Код открытого всплывающего баннера; null — закрыт. */
  openInterstitial: string | null;
  lastInterstitialAt: number;
  /**
   * Сеть уже отказалась рисовать баннер в этом сеансе.
   *
   * Блок привязан к контейнеру по идентификатору, и второй раз тот же блок сеть
   * не рисует: переходы между экранами страницу не перезагружают. Дальше
   * показывать нечего — остаётся только затемнение поверх приложения, и его
   * человек видит при каждом поиске.
   */
  interstitialEmpty: boolean;

  setConfig: (config: AdsConfig) => void;
  /** Баннер оказался пустым: закрыть и больше не пытаться до перезагрузки. */
  markInterstitialEmpty: () => void;
  setScreen: (screen: AdScreenName) => void;
  setMobile: (isMobile: boolean) => void;
  showInterstitial: (trigger: InterstitialTrigger) => void;
  closeInterstitial: () => void;
}

export const useAdsStore = create<AdsState>((set, get) => ({
  config: emptyAdsConfig(),
  isMobile: typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches,
  screen: 'landing',
  openInterstitial: null,
  lastInterstitialAt: 0,
  interstitialEmpty: false,

  setConfig: (config) => set({ config }),
  setScreen: (screen) => set({ screen }),
  setMobile: (isMobile) => set({ isMobile }),

  /**
   * Показ всплывающего баннера.
   *
   * Условий три: для этого устройства задан код, момент разрешён в панели и с
   * прошлого показа прошло достаточно времени. Без последнего объявление
   * появлялось бы после каждого диалога подряд, а короткие разговоры здесь
   * обычное дело.
   */
  markInterstitialEmpty: () => set({ openInterstitial: null, interstitialEmpty: true }),

  showInterstitial: (trigger) => {
    const { config, isMobile, lastInterstitialAt, interstitialEmpty } = get();
    const { interstitial } = config;

    if (interstitialEmpty) {
      return;
    }

    const code = isMobile ? interstitial.code.mobile : interstitial.code.desktop;
    if (!code) {
      return;
    }

    const allowed = trigger === 'chatEnd' ? interstitial.onChatEnd : interstitial.onSearchStart;
    if (!allowed) {
      return;
    }

    const passedMs = Date.now() - lastInterstitialAt;
    if (interstitial.minIntervalSec > 0 && passedMs < interstitial.minIntervalSec * 1000) {
      return;
    }

    set({ openInterstitial: code, lastInterstitialAt: Date.now() });
  },

  closeInterstitial: () => set({ openInterstitial: null }),
}));

/** Ширину окна отслеживаем один раз на приложение. */
export function trackAdDevice(): () => void {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const query = window.matchMedia(MOBILE_QUERY);
  const apply = () => useAdsStore.getState().setMobile(query.matches);

  apply();
  query.addEventListener('change', apply);
  return () => query.removeEventListener('change', apply);
}

/** Код места на экране для текущего устройства. */
export function useScreenSlotCode(name: AdScreenSlotName): string | null {
  return useAdsStore((state) =>
    state.isMobile ? state.config[name].code.mobile : state.config[name].code.desktop,
  );
}

/**
 * Код боковой полосы для текущего экрана.
 *
 * У каждого экрана свой блок: сеть привязывает блок к контейнеру по
 * идентификатору и второй раз тот же не рисует. Пусто — на этом экране полосы
 * нет. Показывается только на компьютере: ниже 1280 px свободного поля по краям
 * не остаётся.
 */
export function useSideSlotCode(name: AdSideSlotName): string | null {
  return useAdsStore((state) => state.config[name].code[state.screen]);
}
